/**
 * Email-domain helpers for the two org-level cascades: tax exemption and
 * partner (Trusted Installer) pricing.
 *
 * Both are keyed by email domain and CASCADE to everyone at that domain
 * (existing + future signups). PUBLIC / free webmail domains (gmail, yahoo,
 * a consumer ISP) must NEVER become an org — thousands of unrelated people
 * share them — so approving for such an address stays per-account.
 * `approveRequest`, `approveApplication` and both org cascades use these
 * helpers to make that call.
 */

/**
 * lower(split_part(email,'@',2)) — the canonical domain key, or null when the
 * address is malformed. Mirrors the SQL the cascade matches on.
 */
export function extractEmailDomain(email: string): string | null {
  const parts = email.trim().split('@');
  if (parts.length !== 2) return null;
  const local = parts[0]?.trim();
  const domain = parts[1]?.trim().toLowerCase();
  if (!local || !domain) return null;
  return domain;
}

/**
 * Known public / free / consumer-ISP webmail domains. An exemption for one of
 * these is granted per-account only — never promoted to an org cascade.
 */
const PUBLIC_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  // Google
  'gmail.com', 'googlemail.com',
  // Microsoft
  'outlook.com', 'hotmail.com', 'hotmail.co.uk', 'live.com', 'msn.com',
  // Yahoo / AOL (Verizon Media)
  'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'rocketmail.com', 'aol.com',
  // Apple
  'icloud.com', 'me.com', 'mac.com',
  // Proton
  'proton.me', 'protonmail.com', 'pm.me',
  // Other consumer webmail
  'gmx.com', 'gmx.net', 'mail.com', 'zoho.com', 'yandex.com', 'yandex.ru',
  'fastmail.com', 'hey.com', 'hushmail.com', 'inbox.com', 'tutanota.com',
  // US ISP webmail
  'comcast.net', 'verizon.net', 'att.net', 'sbcglobal.net', 'bellsouth.net',
  'cox.net', 'charter.net', 'earthlink.net', 'frontier.com', 'optonline.net',
  'roadrunner.com', 'windstream.net', 'centurylink.net',
  // Added from the 2026-08-10 partner backfill review: shared ISP/webmail
  // domains that had accumulated unrelated accounts (shaw.ca alone had 24).
  'shaw.ca', 'cs.com', 'wildblue.net', 'rr.com',
]);

/**
 * Public domains that also own every SUBdomain. Road Runner hands out regional
 * addresses (`nycap.rr.com`, `tampabay.rr.com`, …) that are as public as the
 * apex, so an exact-set match would let each region become its own "company".
 * Matching is on a label boundary — `surr.com` is not Road Runner.
 */
const PUBLIC_EMAIL_DOMAIN_SUFFIXES: readonly string[] = ['.rr.com'];

/** SCW's own domains. Staff accounts are never an org of customers. */
const STAFF_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'getscw.com',
  'security-camera-warehouse.com',
]);

/** True when `domain` is a public / free webmail domain (per-account only). */
export function isPublicEmailDomain(domain: string): boolean {
  const normalized = domain.trim().toLowerCase();
  if (PUBLIC_EMAIL_DOMAINS.has(normalized)) return true;
  return PUBLIC_EMAIL_DOMAIN_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}

/**
 * True when `domain` belongs to SCW itself. Staff addresses must never seed an
 * org cascade: one approved staff application would otherwise put every
 * colleague on partner pricing.
 */
export function isStaffEmailDomain(domain: string): boolean {
  return STAFF_EMAIL_DOMAINS.has(domain.trim().toLowerCase());
}

/**
 * Shape of a domain name: labels of letters/digits/hyphens, at least one dot,
 * never a leading or trailing hyphen on a label.
 *
 * Lives here rather than inline in the Zod schema because the admin domain
 * input needs the SAME rule to tell an admin their entry is malformed before
 * the round trip. A second copy in the component is how "company.comfoo.com"
 * ends up accepted by the form and rejected by the API with a generic message.
 */
const DOMAIN_SYNTAX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/**
 * True when `value` LOOKS like a domain. Syntax only: it says nothing about
 * whether the domain may own a company (that is `isCompanyEmailDomain`) or
 * whether some other company already owns it (only the database knows). A
 * client using this must still treat the server's answer as authoritative.
 */
export function isValidDomainSyntax(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return normalized.length >= 3 && normalized.length <= 255 && DOMAIN_SYNTAX.test(normalized);
}

/**
 * True when this ADDRESS is on free webmail. A reviewer-facing signal, never a
 * gate: since Matthew's 2026-08-19 decision a personal-email applicant is a
 * first-class actor who files requests like anybody else, and the only thing
 * their address changes is that the domain rule cannot link them to a company
 * on its own — so somebody has to approve the membership by hand.
 *
 * Deliberately narrower than `!isCompanyEmailDomain`: a staff address and a
 * malformed one are also refused a domain cascade, but calling either "personal
 * email" at a reviewer would be false.
 */
export function isPersonalEmailAddress(email: string): boolean {
  const domain = extractEmailDomain(email);
  return domain !== null && isPublicEmailDomain(domain);
}

/**
 * True when a domain may own an org (a real company): a well-formed domain
 * that is neither public webmail nor SCW staff. The single question both org
 * cascades ask before promoting one account's approval to a whole domain.
 */
export function isCompanyEmailDomain(domain: string | null | undefined): boolean {
  if (!domain) return false;
  const normalized = domain.trim().toLowerCase();
  if (normalized.length === 0) return false;
  return !isPublicEmailDomain(normalized) && !isStaffEmailDomain(normalized);
}
